import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AVATAR_CROP_VIEW_SIZE,
  AVATAR_MAX_ZOOM,
  AVATAR_MIN_ZOOM,
  AVATAR_TARGET_BYTES,
  clampAvatarOffset,
  coverScale,
  cropAvatarToFile,
  formatBytes,
  loadImageFromFile,
} from "@/lib/cropAvatarImage";

/**
 * Circular crop dialog: drag to pan, slider to zoom, then compress for upload.
 */
export default function AvatarCropDialog({
  open,
  file,
  onOpenChange,
  onCropped,
}) {
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(AVATAR_MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const dragRef = useRef(null);

  const viewSize = AVATAR_CROP_VIEW_SIZE;

  useEffect(() => {
    if (!open || !file) {
      setImage(null);
      setPreviewUrl(null);
      setZoom(AVATAR_MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      setError(null);
      setLoadError(null);
      setBusy(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setLoadError(null);
    loadImageFromFile(file)
      .then(({ image: img, objectUrl: url }) => {
        objectUrl = url;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setImage(img);
        setPreviewUrl(url);
        setZoom(AVATAR_MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.message || "Could not load image");
          setImage(null);
          setPreviewUrl(null);
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, file]);

  const setClampedOffset = useCallback(
    (x, y, nextZoom = zoom) => {
      if (!image) return;
      const clamped = clampAvatarOffset(
        image.naturalWidth,
        image.naturalHeight,
        viewSize,
        nextZoom,
        x,
        y
      );
      setOffset({ x: clamped.offsetX, y: clamped.offsetY });
    },
    [image, viewSize, zoom]
  );

  const onZoomChange = (value) => {
    const nextZoom = Number(value);
    setZoom(nextZoom);
    setClampedOffset(offset.x, offset.y, nextZoom);
  };

  const onPointerDown = (e) => {
    if (!image || busy) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    setClampedOffset(drag.originX + dx, drag.originY + dy);
  };

  const onPointerUp = (e) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  const onWheel = (e) => {
    if (!image || busy) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    const nextZoom = Math.min(
      AVATAR_MAX_ZOOM,
      Math.max(AVATAR_MIN_ZOOM, zoom + delta)
    );
    setZoom(nextZoom);
    setClampedOffset(offset.x, offset.y, nextZoom);
  };

  const handleApply = async () => {
    if (!image || !file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const cropped = await cropAvatarToFile({
        image,
        viewSize,
        zoom,
        offsetX: offset.x,
        offsetY: offset.y,
        maxBytes: AVATAR_TARGET_BYTES,
        fileName: file.name || "avatar.jpg",
      });
      await onCropped?.(cropped);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || "Could not crop image");
    } finally {
      setBusy(false);
    }
  };

  const scale = image
    ? coverScale(image.naturalWidth, image.naturalHeight, viewSize) * zoom
    : 1;
  const drawnW = image ? image.naturalWidth * scale : 0;
  const drawnH = image ? image.naturalHeight * scale : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust photo</DialogTitle>
          <DialogDescription>
            Drag to reposition, zoom to fill the circle. We compress the result
            for upload (target under {formatBytes(AVATAR_TARGET_BYTES)}).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div
            className="relative touch-none select-none overflow-hidden rounded-full bg-muted shadow-inner cursor-grab active:cursor-grabbing"
            style={{ width: viewSize, height: viewSize }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            role="presentation"
          >
            {image && previewUrl ? (
              <img
                src={previewUrl}
                alt=""
                draggable={false}
                className="pointer-events-none absolute max-w-none"
                style={{
                  width: drawnW,
                  height: drawnH,
                  left: viewSize / 2 + offset.x - drawnW / 2,
                  top: viewSize / 2 + offset.y - drawnH / 2,
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                {loadError || "Loading…"}
              </div>
            )}
            <div
              className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-white/40"
              aria-hidden
            />
          </div>

          <div className="w-full space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="avatar-zoom">Zoom</Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {zoom.toFixed(2)}×
              </span>
            </div>
            <input
              id="avatar-zoom"
              type="range"
              min={AVATAR_MIN_ZOOM}
              max={AVATAR_MAX_ZOOM}
              step={0.01}
              value={zoom}
              disabled={!image || busy}
              onChange={(e) => onZoomChange(e.target.value)}
              className="w-full accent-primary"
            />
          </div>

          {file ? (
            <p className="text-xs text-muted-foreground w-full">
              Original {formatBytes(file.size)}
              {file.size > AVATAR_TARGET_BYTES
                ? " — will be compressed on apply"
                : ""}
            </p>
          ) : null}

          {(error || loadError) && (
            <p className="text-sm text-destructive w-full">{error || loadError}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!image || busy || !!loadError}
            onClick={handleApply}
          >
            {busy ? "Compressing…" : "Use photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
